import { debounce, get } from "lodash";
import React, { createContext } from "react";
import { customFilter, FILTER_TYPES } from "react-bootstrap-table2-filter";
import { FormattedMessage } from "react-intl";
import { MultiSelectSearch } from "../MultiSelectSearch";
import { FILTER_DEBOUNCE_TIME } from "./constants";
import "./Table.scss";
import TextFilter from "./TextFilter";

export const FilterContext = createContext({});

// react-bootstrap-table2 is very limited
// it doesn't pass by default the column that is being filtered on the custom onFilter
const packOnFilter = (onFilter, column, value, remote) => {
  if (remote) {
    onFilter(value);
  } else {
    onFilter({ dataField: column.dataField, value });
  }
};

/* TEXT FILTER RENDERER */
const renderTextFilter = (onFilter, column) => (
  <FilterContext.Consumer>
    {(filters) => (
      <TextFilter
        placeholder={column.text}
        onChange={debounce(
          (value) => packOnFilter(onFilter, column, value, filters),
          FILTER_DEBOUNCE_TIME
        )}
        remoteValue={filters?.[column.dataField]?.filterVal}
        getFilter={column?.filter?.props?.getFilter}
      />
    )}
  </FilterContext.Consumer>
);

/* MULTISELECT FILTER RENDERER */
const getOptionLabel = (option, column) => {
  if (option.label) {
    return option.label;
  }

  if (option.value === "") {
    return (
      <FormattedMessage
        id="UIC.TABLE.NO_OPTION"
        values={{ label: column.text.toLowerCase() }}
      />
    );
  }

  return option.value;
};

const renderMultiSelectSearch = (onFilter, column) => (
  <FilterContext.Consumer>
    {(filters) => (
      <MultiSelectSearch
        id={column.dataField}
        name={column.dataField}
        placeholder={column.text}
        remoteValue={filters?.[column.dataField]?.filterVal}
        onChange={(value) => packOnFilter(onFilter, column, value, filters)}
        options={get(column, "filter.props.options", []).map((option) => ({
          ...option,
          label: getOptionLabel(option, column),
        }))}
        getFilter={column?.filter?.props?.getFilter}
        className="Uic-Table-multiselectFilter"
      />
    )}
  </FilterContext.Consumer>
);

/* FILTER RENDERER CONSTRUCTOR */
export const FILTER_RENDERERS = {
  [FILTER_TYPES.TEXT]: renderTextFilter,
  [FILTER_TYPES.MULTISELECT]: renderMultiSelectSearch,
};

export const createFilterRenderer = (filter, filterRenderer) => {
  // is using one of our custom filters
  if (filter?.type) {
    return FILTER_RENDERERS[filter.type];
  }

  // is using bootstrap filter
  return filterRenderer;
};

/* CUSTOM FILTER CONFIG */
// reference: https://react-bootstrap-table.github.io/react-bootstrap-table2/storybook/index.html?selectedKind=Column%20Filter&selectedStory=Implement%20custom%20filter%20logic&full=0&addons=1&stories=1&panelRight=0&addonPanel=storybook%2Factions%2Factions-panel

// matches substring ignoring case
const onFilterText = (filterValue) => (filtered, data) => {
  const { dataField, value } = filtered ?? {};
  if (value) {
    const lowercasedValue = String(value).toLowerCase();
    return data?.filter((item) => {
      let fieldValue = item[dataField];

      // support filterValue https://react-bootstrap-table.github.io/react-bootstrap-table2/docs/column-props.html#columnfiltervalue-function
      if (filterValue) {
        fieldValue = filterValue(item[dataField], item);
      }

      const lowercasedData = String(fieldValue).toLowerCase();
      return lowercasedData.includes(lowercasedValue);
    });
  }
  return data;
};

// matches the exact values that were selected
const onFilterMultiSelect = (filterValue) => (filtered, data) => {
  const { dataField, value } = filtered ?? {};
  // value is always an array on multiselect fields
  if (value?.length) {
    const valueMap = value.reduce((acc, val) => ({ ...acc, [val]: true }), {});

    return data?.filter((item) => {
      let fieldValue = item[dataField];

      // support filterValue https://react-bootstrap-table.github.io/react-bootstrap-table2/docs/column-props.html#columnfiltervalue-function
      if (filterValue) {
        fieldValue = filterValue(item[dataField], item);
      }

      // the row's field value could be either an array of strings or a single string
      if (Array.isArray(fieldValue)) {
        return fieldValue.some((fieldVal) => valueMap[fieldVal]);
      }

      return valueMap[fieldValue];
    });
  }
  return data;
};

const ON_FILTER = {
  [FILTER_TYPES.TEXT]: onFilterText,
  [FILTER_TYPES.MULTISELECT]: onFilterMultiSelect,
};

export const createFilter = (filter, filterValue) => {
  // is using one of our custom filters
  if (filter?.type) {
    return customFilter({
      ...filter,
      onFilter: ON_FILTER[filter.type](filterValue),
    });
  }

  // is using bootstrap filter
  return filter;
};
